import type { QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';

import { Toaster } from './components/toaster';
import { AppShell } from './components/app-shell';
import { ApiError } from './lib/api';
import { MODULES } from './lib/modules';
import { meQuery } from './lib/queries';
import { NotFoundPage } from './components/not-found';
import { RouteError } from './components/route-error';
import { ConfigPage } from './pages/config';
import { InboxPage } from './pages/inbox';
import { LeadsPage } from './pages/leads';
import { OnboardingSection } from './pages/onboarding';
import { PromptsPage } from './pages/prompts';
import { SubscriptionPage } from './pages/subscription';
import {
  BillingSettingsSection,
  InvoicesSection,
  MarginsSection,
  PaymentsWithProofSection,
  PlansAndAddonsSection,
  SubscriptionBillingPanel,
} from './pages/superadmin-billing';
import { TeamPage } from './pages/team';
import {
  ForgotPage,
  InvitationPage,
  LoginPage,
  MfaEnrollPage,
  MfaVerifyPage,
  ResetPage,
} from './pages/auth';
import { AlertsPage, AnalyticsPage, HistoryPage, OpsPage } from './pages/console';
import { OverviewPage } from './pages/overview';
import {
  AuditSection,
  ClientsSection,
  SubscriptionsSection,
  SuperadminLayout,
  TenantsSection,
  UsersSection,
} from './pages/superadmin';

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <Outlet />
      <Toaster />
    </>
  ),
});

const tokenSearch = (s: Record<string, unknown>): { token?: string } =>
  typeof s.token === 'string' ? { token: s.token } : {};

// ------------------------------------------------------------------ públicas
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});
const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/2fa',
  component: MfaVerifyPage,
});
const enrollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/2fa/configurar',
  component: MfaEnrollPage,
});
const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/olvide',
  component: ForgotPage,
});
const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/restablecer',
  validateSearch: tokenSearch,
  component: ResetPage,
});
const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitacion',
  validateSearch: tokenSearch,
  component: InvitationPage,
});

// ------------------------------------------------------------------ app autenticada
/**
 * Layout protegido. Resuelve /api/me: sin sesión → /login; 2FA pendiente → paso que falte.
 * El `?scope=` se valida acá y lo heredan todas las pantallas.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  validateSearch: (s: Record<string, unknown>): { scope?: string } =>
    typeof s.scope === 'string' ? { scope: s.scope } : {},
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.query(meQuery);
      if (me.mfa === 'pending') throw redirect({ to: '/login/2fa' });
      if (me.mfa === 'enroll_required') throw redirect({ to: '/login/2fa/configurar' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) throw redirect({ to: '/login' });
      throw err;
    }
  },
  component: AppShell,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/overview' });
  },
});
const overviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/overview',
  component: OverviewPage,
});
const configRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/config',
  component: ConfigPage,
});
const teamRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/equipo',
  component: TeamPage,
});

const consoleRoutes = (
  [
    ['analytics', AnalyticsPage],
    ['history', HistoryPage],
    ['ops', OpsPage],
    ['alerts', AlertsPage],
    ['inbox', InboxPage],
    ['leads', LeadsPage],
    ['prompts', PromptsPage],
    ['subscription', SubscriptionPage],
  ] as const
).map(([id, component]) => {
  const mod = MODULES.find((m) => m.id === id);
  if (!mod) throw new Error(`Módulo desconocido: ${id}`);
  return createRoute({ getParentRoute: () => appRoute, path: mod.path, component });
});

const superadminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/superadmin',
  component: SuperadminLayout,
});
const superIndex = createRoute({
  getParentRoute: () => superadminRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/superadmin/clientes' });
  },
});
const superChildren = [
  createRoute({
    getParentRoute: () => superadminRoute,
    path: '/clientes',
    component: ClientsSection,
  }),
  createRoute({
    getParentRoute: () => superadminRoute,
    path: '/empresas',
    component: TenantsSection,
  }),
  createRoute({
    getParentRoute: () => superadminRoute,
    path: '/nueva-empresa',
    component: OnboardingSection,
  }),
  createRoute({
    getParentRoute: () => superadminRoute,
    path: '/usuarios',
    component: UsersSection,
  }),
  createRoute({
    getParentRoute: () => superadminRoute,
    path: '/planes',
    component: PlansAndAddonsSection,
  }),
  createRoute({
    getParentRoute: () => superadminRoute,
    path: '/suscripciones',
    component: () => (
      <SubscriptionsSection
        renderActions={(id) => <SubscriptionBillingPanel subscriptionId={id} />}
      />
    ),
  }),
  createRoute({
    getParentRoute: () => superadminRoute,
    path: '/facturas',
    component: InvoicesSection,
  }),
  createRoute({
    getParentRoute: () => superadminRoute,
    path: '/pagos',
    component: PaymentsWithProofSection,
  }),
  createRoute({
    getParentRoute: () => superadminRoute,
    path: '/margenes',
    component: MarginsSection,
  }),
  createRoute({
    getParentRoute: () => superadminRoute,
    path: '/facturacion',
    component: BillingSettingsSection,
  }),
  createRoute({
    getParentRoute: () => superadminRoute,
    path: '/auditoria',
    component: AuditSection,
  }),
];

export const routeTree = rootRoute.addChildren([
  loginRoute,
  mfaRoute,
  enrollRoute,
  forgotRoute,
  resetRoute,
  invitationRoute,
  appRoute.addChildren([
    indexRoute,
    overviewRoute,
    configRoute,
    teamRoute,
    ...consoleRoutes,
    superadminRoute.addChildren([superIndex, ...superChildren]),
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Un error de una pantalla se muestra DENTRO del shell (menú y selector siguen andando).
    defaultErrorComponent: RouteError,
    defaultNotFoundComponent: NotFoundPage,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
